import { useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@/features/auth'
import { useLocalizedNavigate } from '@/shared/hooks'
import { SEO } from '@/shared/components'
import { PrecadrageLayout, PrecadrageForm, type PrecadrageFormRef } from '../components'
import { useSubmitPrecadrage } from '../hooks'

export function PrecadragePage() {
  const { user, isLoading } = useAuth()
  const navigate = useLocalizedNavigate()
  const { submit, isSubmitting, error } = useSubmitPrecadrage()
  const formRef = useRef<PrecadrageFormRef>(null)

  // If user is authenticated, redirect to dashboard/new-project
  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, isLoading, navigate])

  // Handle field injection from AI assistant
  const handleInjectField = useCallback((field: string, content: string) => {
    formRef.current?.injectField(field, content)
  }, [])

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  // Don't render form if user is authenticated (will redirect)
  if (user) {
    return null
  }

  return (
    <PrecadrageLayout onInjectField={handleInjectField}>
      <SEO
        titleKey="seo.precadrage.title"
        descriptionKey="seo.precadrage.description"
      />
      <div className="flex flex-1 items-start justify-center overflow-y-auto">
        <div className="w-full max-w-[560px]">
          <PrecadrageForm
            ref={formRef}
            onSubmit={submit}
            isSubmitting={isSubmitting}
            error={error}
          />
        </div>
      </div>
    </PrecadrageLayout>
  )
}
